import { React, useState, useEffect, useContext, useRef } from 'react';
import { Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import JSON5 from 'json5';
import { jsonrepair } from 'jsonrepair';
import { Select, ConfigProvider, Input, Flex, Modal, Space } from 'antd';
import { Pen, DashCircle, ArrowClockwise, ArrowLeft, Trash3, PlusLg, Copy } from 'react-bootstrap-icons';
import { PauseCircleOutlined } from '@ant-design/icons';

import { AuthContext } from '../../services/auth.service';
import { simplifyDLGEventJson } from './index';
import { transformDataObject, NestedObjectTable } from '../../components/elements/JsonObjTable';
import { DropdownMenu } from '../../components/elements/DropdownMenu';
import { ActionButton } from '../../components/elements/Button';
import colors from '../../styles/_colors.scss';

import {
    ProdSettingsContext,
    DataLayerEventsContext,
    MonitoringContext,
} from './index';

import { defaultMenuProps } from './defaultEventTemplates';

const { Option } = Select;
const { TextArea } = Input;

const generateUniqueId = () => uuidv4();


const addUniqueIdRows = (data) => {
    const newList = [];

    data?.forEach((obj) => {
        const newObj = { ...obj };
        if (!(newObj.hasOwnProperty('uniqueId'))) {
            newObj.uniqueId = generateUniqueId();
        }
        newList.push(newObj);
    });

    return newList;
};


export const ProductSettinsModal = () => {
    const {
        companyId,
        productDescLink,
        dlgCheckFreq,
        curCheckFreq,
        setCurCheckFreq,
        setConfigParams,
        companyDomains,
        setCompanyDomains,
        curTimezone = "UTC", // fallback for no value provided
    } = useContext(ProdSettingsContext);

    const [choosenTimezone] = useState(curTimezone);
    const [choosenCheckFreq, setChoosenCheckFreq] = useState(curCheckFreq);
    const [configParamsToSave, setConfigParamsToSave] = useState({});
    const [showGuideDivDomain, setShowGuideDivDomain] = useState(false);


       // helper to normalize each domain object
   const formatDomain = d => ({
     domain:      d.domain      || "",
     sitemap_url: d.sitemap_url || "",
    // ← derive sitemapType if it wasn’t explicitly set
    sitemapType: d.sitemapType ?? (d.sitemap_url.trim() ? "add_url" : "no_url")
   });

    const [curCompanyDomains, setCurCompanyDomains] = useState(() => {
    // if there’s at least one saved domain, normalize them
    if (companyDomains.length > 0) {
        return companyDomains.map(formatDomain);
    }
    // otherwise start with a single blank row
    return [{ domain: "", sitemap_url: "", sitemapType: "no_url" }];
    })

    // State variables for include exclude pages
    const [includeType, setIncludeType] = useState("Include");
    const [pageType, setPageType] = useState("hostname");
    const [matchType, setMatchType] = useState("exactly matches");
    const [filterValue, setFilterValue] = useState("");


    const { adminUser } = useContext(AuthContext);


    useEffect(() => {
    if (companyDomains.length > 0) {
        setCurCompanyDomains(companyDomains.map(formatDomain));
    } else {
        setCurCompanyDomains([{ domain: "", sitemap_url: "", sitemapType: "no_url" }]);
    }
    }, [companyDomains]);

    const handleCheckFreqChange = (newCheckFreq) => {
        setCurCheckFreq(newCheckFreq);
        setChoosenCheckFreq(newCheckFreq);
    }

    const handleElemAdd = () => {
        const newDomain = { domain: "", sitemapType: "no_url", sitemap_url: "" };
        setCurCompanyDomains([...curCompanyDomains, newDomain]);
        setCompanyDomains([...curCompanyDomains, newDomain]);
    };

    const handleElemDelete = (idx) => {
        const newDomainsList = curCompanyDomains?.filter((elem, index) => index !== idx);
        setCurCompanyDomains(newDomainsList);
        setCompanyDomains(newDomainsList)
    }

    const handlePauseClick = () => {
        // Your logic here
    };

    // Handle changes in the domain fields
    const handleDomainFieldChange = (idx, field, valueOrEvent) => {
        const value = valueOrEvent && valueOrEvent.target ? valueOrEvent.target.value : valueOrEvent;
            const newDomainsList = [...curCompanyDomains];
            const elem = { ...newDomainsList[idx] };
            elem[field] = value;

            // If changing the dropdown to "no_url", clear the sitemap_url
            if (field === "sitemapType" && value === "no_url") {
                elem.sitemap_url = "";
            }

            newDomainsList[idx] = elem;
            setCurCompanyDomains(newDomainsList);
            setCompanyDomains(newDomainsList);
    };

    useEffect(() => {
        const newObject = { ...configParamsToSave };
        newObject.timezone = choosenTimezone;
        newObject.check_frequency = choosenCheckFreq;
        newObject.company_domains = curCompanyDomains;
        setConfigParamsToSave(newObject);
    }, [choosenTimezone, choosenCheckFreq, curCompanyDomains]);

    useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

    return (
        <div key={companyId} className="modal-elems-rows" id='products-settings-main' style={{ overflow: 'visible',  overflowX: 'visible' }}>

            {/* Pause DataLayer Guard */}
            {/* TODO: Uncomment when the pause functionality is implemented */}
            {/*
            <div
            style={{
                position: 'absolute',
                top: 18,
                right: 32,
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                zIndex: 2
            }}
            onClick={handlePauseClick}
            >
            <PauseCircleOutlined
                style={{
                fontSize: 14,
                color: "#222",
                fontWeight: 900,
                marginRight: 10,
                filter: "contrast(200%) brightness(60%) drop-shadow(0 0 0.5px #222)"
                }}
            />
            <span style={{
                fontWeight: 550,
                color: '#222',
                fontSize: 12,
                letterSpacing: 0.1
            }}>
                Deactivate DataLayer Guard
            </span>
            </div>
            */}

            <div>
                <h2 className='header-letter-spacing'>General Settings</h2>
            </div>
            <div className="modal-elems">
                <div className="container-guide modal-panel-button-row">
                    <Link className="modal-guide-banner blue-bg" style={{ borderColor: colors.ccBlue }} target="_blank" to={productDescLink}>
                        <div className="file-icon" style={{ color: colors.ccBlue }}>
                            <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g filter="url(#filter0_d_190_157)">
                                    <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                    <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                    <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                    <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                    <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                    <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                    <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                    <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                    <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                    <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                    <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                </g>
                                <defs>
                                    <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                        <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                        <feOffset dy="1.74496" />
                                        <feGaussianBlur stdDeviation="0.872482" />
                                        <feComposite in2="hardAlpha" operator="out" />
                                        <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                        <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                    </filter>
                                </defs>
                            </svg>


                        </div>
                        <div className="panel-button-text">
                            <div className="panel-button-desc">Need help? Check our <u>implementation guide</u> for detailed configuration instructions and best practices for DataLayer Guard. Question not covered? Please <u>get in contact</u></div>
                        </div>
                    </Link>
                </div>
                    <div className="modal-text">
                        <p>
                            <br/>
                            This page holds all general settings for the DataLayer Guard. Please start with the settings below to start the configuration.
                        </p>         
                    </div>
            </div>

            {/* Domains and sitemap section */}
            <div className="elems-list">
                {showGuideDivDomain &&
                    <div className='hoverable-div-parent' style={{ left: '6.8em' }}>
                        <p className='hover-content'>
                            Only the domain(s) added to this list will be
                            activated for alerting and will be shown in the
                            dashboards.
                        </p>
                    </div>
                }
                <h3 style={{ marginBottom: 0 }} className="modal-category">
                    Domains and sitemap
                </h3>
                <p style={{ marginBottom: 0 }}>
                    Add the domain(s) for which you would like to activate the DataLayer Guard.
                </p>
                <div className="list-col-2-add-dl" style={{ width: '45em' }}>
                    {curCompanyDomains?.map((elem, idx) =>
                        <div key={idx} className="inputs-row-3-col-single d-flex flex-row" style={{ alignItems: "center", gap: "8px" }}>
                            <Input
                                type="text"
                                placeholder="domain name"
                                value={elem?.domain || ""}
                                onChange={event => handleDomainFieldChange(idx, "domain", event)}
                                style={{ width: 160, minWidth: 160, maxWidth: 160 }}
                            />
                            <Select
                                value={elem.sitemapType || "no_url"}
                                style={{ width: 220, minWidth: 220, maxWidth: 220 }}
                                onChange={val => handleDomainFieldChange(idx, "sitemapType", val)}
                            >
                                <Option value="add_url">Add sitemap URL</Option>
                                <Option value="no_url">No Sitemap URL available</Option>
                            </Select>
                            {adminUser && (
                                <Input
                                    type="text"
                                    placeholder="add sitemap URL"
                                    value={elem?.sitemap_url || ""}
                                    disabled={elem.sitemapType !== "add_url"}
                                    onChange={event => handleDomainFieldChange(idx, "sitemap_url", event)}
                                    style={{ width: 270, minWidth: 270, maxWidth: 270 }}
                                />
                            )}
                            <div>
                                <DashCircle className='color-blue' size={18} onClick={() => handleElemDelete(idx)} />
                            </div>
                        </div>
                    )}
                </div>
                
                   <div>
                    {/* Add domain button */}           
                    <button
                        style={{
                            background: "transparent",
                            color: "#232821",
                            border: "none",
                            fontWeight: 600,
                            fontSize: "12px",
                            padding: "4px 0", 
                            marginLeft: "15px",
                            letterSpacing: "0.1px",
                            position: "relative",
                            cursor: "pointer",
                            outline: "none",
                            boxShadow: "none",
                            lineHeight: "12px",
                            minWidth: "auto"
                            
                        }}
                        onClick={handleElemAdd}
                        >
                        + add domain
                        <span style={{
                            display: "block",
                            width: "100%",
                            height: "3px",
                            background: "#cfff5e",       // Neon green
                            borderRadius: "2px",
                            position: "absolute",
                            left: 0,
                            bottom: 0
                        }} />
                    </button>
                </div>

                {/* Include or exclude pages section */}
                {/* TODO: Uncomment when the pause functionality is implemented */}
                {/*
                <div className="elems-list" style={{ marginTop: '2em' }}>
                {showGuideDivDomain &&
                    <div className='hoverable-div-parent' style={{ left: '6.8em' }}>
                    </div>
                }
                <h3 style={{ marginBottom: 0 }} className="modal-category">
                   Include or exclude pages from the scraper
                </h3>
                <p style={{ marginBottom: 0 }}>
                    The exact trigger for each scenario is defined in the scenario details, available in the next step.<br/>
                    To exclude or include specific page(groups) for the scraper, please provide the details below.
                </p>
                    <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
                        <Select
                        value={includeType}
                        style={{ width: 120 }}
                        onChange={val => setIncludeType(val)}
                        >
                        <Option value="Include">Include</Option>
                        <Option value="Exclude">Exclude</Option>
                        </Select>
                        <Select
                        value={pageType}
                        style={{ width: 150 }}
                        onChange={val => setPageType(val)}
                        >
                        <Option value="hostname">hostname</Option>
                        </Select>
                        <Select
                        value={matchType}
                        style={{ width: 160 }}
                        onChange={val => setMatchType(val)}
                        >
                        <Option value="exactly matches">exactly matches</Option>
                        <Option value="matches regex">contains</Option>
                        <Option value="contains">contains</Option>
                        </Select>
                        <Input
                        value={filterValue}
                        placeholder="/DataLayer-Guard"
                        onChange={e => setFilterValue(e.target.value)}
                        style={{ width: 160 }}
                        disabled={matchType !== "exactly matches" && matchType !== "contains"}
                        />
                    </div>
                </div>
             */}
             
            </div>
            {/* Run frequency section */}
            <div>
                <h3 style={{ marginBottom: 0 }} className="modal-category">Run frequency</h3>
                <p>How often should the DataLayer Guard run?</p>
                <div>
                    <Select
                        style={{ width: '100%', maxWidth: '150px' }}
                        placeholder="Choose frequency"
                        value={choosenCheckFreq}
                        onChange={handleCheckFreqChange}
                        popupClassName="custom-dropdown"
                    >
                        {Object.keys(dlgCheckFreq).map(key => (
                            <Option key={key} value={key}>
                                {dlgCheckFreq[key]}
                            </Option>
                        ))}
                    </Select>
                </div>
            </div>
        </div>
    );
}

export const DataLayerEventsModal = () => {
    const {
        companyId,
        scrapedEventsDescLink,
        dlEvents,
        setDlEvents,
        setConfigParams,
        companyDomains,
        newTemplateVersionAvailable,
        pushDefaultEcommerceObject,
        cleanJsonObject,
        handleEventAdd,
        handleEventRemove,
        error,
    } = useContext(DataLayerEventsContext);

    const [curEvents, setCurEvents] = useState(addUniqueIdRows(dlEvents));
    const [configParamsToSave, setConfigParamsToSave] = useState({});
    const [selectedCompanyDoamain, setSelectedCompanyDoamain] = useState("All domains");
    const [filteredCurEvents, setFilteredCurEvents] = useState(curEvents);

    const { adminUser } = useContext(AuthContext);

    const [editedEventIdx, setEditedEventIdx] = useState(null);
    const [editedEventName, setEditedEventName] = useState("");
    const [element, setElement] = useState("");
    const [urlMatchType, setUrlMatchType] = useState("");
    const [elementMatchType, setElementMatchType] = useState("");
    const [elementRegexValue, setElementRegexValue] = useState("");
    const [editedEventType, setEditedEventType] = useState("");
    const [editedEventStatus, setEditedEventStatus] = useState("");
    const [editedEventUrls, setEditedEventUrls] = useState("");
    const [editedEventTemplate, setEditedEventTemplate] = useState("");
    const [editedEventTemplateNewVersion, setEditedEventTemplateNewVersion] = useState("");

    const [modalContentCode, setModalContentCode] = useState("main");

    const [modalState, setModalState] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalContent, setModalContent] = useState(<></>);
    const [editedTextfieldValue, setEditedTextfieldValue] = useState("");

    const [jsonBtnText, setJsonBtnText] = useState("Add JSON");
    const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusLg />);


    // Add ecommerce event button functionality
    const [isTemplateObjectEmpty, setIsTemplateObjectEmpty] = useState(false);
    useEffect(() => {
        switch (editedEventTemplateNewVersion) {
            case "":
                setIsTemplateObjectEmpty(true);
                break;
            default:
                setIsTemplateObjectEmpty(editedEventTemplateNewVersion?.length > 1);
        }
    }, [editedEventTemplate, editedEventTemplateNewVersion]);

    const menuProps = {
        items: defaultMenuProps,
        onClick: (e) => handlePushDefaultEcommerceObject(e?.key, editedEventIdx),
    };

    const handlePushDefaultEcommerceObject = (key, idx) => {
        const curEventsItem = curEvents.find(elem => elem.uniqueId === idx);
        const newTemplates = pushDefaultEcommerceObject(key, idx, curEventsItem);

        setEditedEventTemplate(newTemplates);
        setEditedEventTemplateNewVersion(transformDataObject(newTemplates));
    }

    const handleCleanJsonTemplate = () => {
        const curEventsItem = curEvents.find(elem => elem.uniqueId === editedEventIdx);
        cleanJsonObject(editedEventIdx, curEventsItem);

        setEditedEventTemplate("");
        setEditedEventTemplateNewVersion("");
    }


    useEffect(() => {
        // check if the new template version exist if not update the events to add it
        if (!newTemplateVersionAvailable) {
            dlEvents?.forEach((event) => {
                const templatesCopy = event.templates;
                let parsedTemplate;

                switch (typeof templatesCopy) {
                    case "string":
                        const repairedJson = templatesCopy && templatesCopy !== '' ? jsonrepair(templatesCopy) : '';
                        parsedTemplate = repairedJson !== '' ? JSON5.parse(repairedJson) : {};
                        break;
                    default:
                        parsedTemplate = templatesCopy;
                }

                const newVersionTemplateObject = transformDataObject(parsedTemplate);
                event.main_templates = newVersionTemplateObject;
            })
        }

        setCurEvents(addUniqueIdRows(dlEvents) || []);
        setFilteredCurEvents(addUniqueIdRows(dlEvents) || []);
        setConfigParamsToSave({ templates: { "events": dlEvents } });
    }, [dlEvents]);


    const updateParamsToSave = (params) => {
        const { newEventsList } = params;
        const newConfigParams = { ...configParamsToSave };
        const curEventsListStr = { "events": newEventsList };
        newConfigParams.templates = curEventsListStr;

        setConfigParamsToSave(newConfigParams);
    }


    const handleEditEventClick = (elem, uniqueId) => {
        setEditedEventIdx(uniqueId);
        setEditedEventName(elem?.event_name || "");
        setElement(elem?.element || "");
        setUrlMatchType(elem?.url_match_type || "");
        setElementMatchType(elem?.element_match_type || "");
        setElementRegexValue(elem?.element_regex_value || "");
        setEditedEventType(elem?.event_type || "");
        setEditedEventStatus(elem?.event_status || "");
        setEditedEventUrls(elem?.urls || "");
        setEditedEventTemplate(elem?.main_templates || elem?.templates || "");
        setEditedEventTemplateNewVersion(elem?.main_templates || "")
        setModalContentCode("edit");
    }

    // update the useStatet parameters when switching between companies
    useEffect(() => {
        setEditedEventIdx();
        setEditedEventName("");
        setElement("");
        setUrlMatchType("");
        setElementMatchType("");
        setElementRegexValue("");
        setEditedEventType("");
        setEditedEventStatus("");
        setEditedEventUrls("");
        setEditedEventTemplate("");
        setEditedEventTemplateNewVersion("");
        setModalContentCode("main");
    }, [companyId]);


    useEffect(() => {
        const newEventsList = curEvents || [];
        const updatingElem = newEventsList.find(elem => elem.uniqueId === editedEventIdx);

        if (updatingElem) {
            updatingElem.event_name = editedEventName;
            updatingElem.element = element;
            updatingElem.url_match_type = urlMatchType;
            updatingElem.element_match_type = elementMatchType;
            updatingElem.element_regex_value = elementRegexValue;
            updatingElem.event_type = editedEventType;
            updatingElem.event_status = editedEventStatus;
            updatingElem.urls = editedEventUrls;
            updatingElem.templates = editedEventTemplate;
            updatingElem.main_templates = editedEventTemplateNewVersion;

            setCurEvents(newEventsList);
            setDlEvents(newEventsList);
            updateParamsToSave({ newEventsList });
        }

        const template = editedEventTemplate !== "" &&
            typeof (editedEventTemplate) === "string" ? JSON5.parse(jsonrepair(editedEventTemplate)) : editedEventTemplate
        const initialTemplate = template?.length === 1 && template[0].key === "" && template[0].value === "";
        const text = initialTemplate ? "Add JSON" : "Edit JSON";
        const icon = initialTemplate ? <PlusLg /> : <Pen />;
        setJsonBtnText(text);
        setJsonBtnIcon(icon)

        setModalContent(defaultModalContent(editedEventIdx, editedEventTemplate));

    }, [modalContentCode, editedEventName,
        editedEventType, editedEventStatus,
        editedEventUrls, editedEventTemplate,
        urlMatchType, element, elementMatchType,
        elementRegexValue
    ]);

    const handleHeaderClick = () => {
        // removes empty events from the list of events
        const newEventsList = curEvents?.filter((elem) =>
            elem.element !== "" ||
            elem.event_name !== "" ||
            elem.urls !== "" ||
            elem.element_regex_value !== "" ||
            elem.main_templates.length > 1
        );

        setCurEvents(() => newEventsList);
        setDlEvents(newEventsList);
        updateParamsToSave({ newEventsList });
        setModalContentCode("main");
    };

    const typesOptions = {
        "": "type",
        "page_load": "page load",
        "interaction": "click",
    };

    const elementOptions = {
        "html_class": "HTML class",
        "html_element": "HTML element",
        "html_id": "HTML ID",
    }

    const urlOptions = {
        "matches_regex": "matches regex",
        "contains ": "contains ",
        "exact_matches": "exact matches ",
    }

    const statusesOptions = [
        {
            label: "Status",
            key: "",
        },
        {
            label: "active",
            key: "active",
        },
        {
            label: "inactive",
            key: "inactive",
        },
    ];
    const statusesProps = {
        items: statusesOptions,
        onClick: (e) => setEditedEventStatus(e?.key),
    };


    const eventAdd = () => {
        const {
            newEvent,
            newEventsList,
            newEventUniqueId,
        } = handleEventAdd();

        setEditedEventTemplate(null);
        setEditedEventTemplateNewVersion(null);
        setCurEvents(newEventsList);
        setModalContentCode("edit");
        updateParamsToSave({ newEventsList });
        handleEditEventClick(newEvent, newEventUniqueId);
    }

    const eventRemove = (idx) => {
        const newEventsList = handleEventRemove(idx);

        setCurEvents(() => newEventsList);
        setModalContentCode("main");
        updateParamsToSave({ newEventsList });
    }


    useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

    // Center modal functionality
    const defaultModalContent = (idx, templates = "") => {
        let defaultValueTemplate = "";
        let parsedTemplate = {};

        const placeholderObj = {
            "event": "event_name",
            "ecommerce": {
                "currency": "string",
                "value": "number",
                "items": [
                    {
                        "item_id": "string",
                        "item_name": "string",
                    }
                ]
            }
        };
        const placeholderText = JSON.stringify(placeholderObj, undefined, 2);

        switch (typeof templates) {
            case "object":
                parsedTemplate = templates;
                break;
            default:
                const repairedJson = templates && templates !== '' ? jsonrepair(templates) : '';
                parsedTemplate = repairedJson !== '' ? JSON5.parse(repairedJson) : {};
        }

        const templateIsArray = Array.isArray(parsedTemplate);

        switch (templateIsArray) {
            case true:
                const tempLn = parsedTemplate.length;

                if (tempLn === 0) {
                    defaultValueTemplate = "";
                }
                else if (tempLn === 1 && parsedTemplate[0].key === "" &&
                    parsedTemplate[0].value === "") {
                    defaultValueTemplate = "";
                }
                else if (Object.keys(parsedTemplate).length > 0) {
                    const simplifiedDLGEventJson = simplifyDLGEventJson(parsedTemplate);
                    defaultValueTemplate = JSON.stringify(simplifiedDLGEventJson, null, 2);
                }
                break;
            default:
                defaultValueTemplate = JSON.stringify(parsedTemplate, null, 2);
        }

        setModalTitle("Template DataLayer object");

        return (
            <>
                <p>Add your dataLayer template in the box below.</p>
                <TextArea
                    key={idx}
                    rows={12}
                    placeholder={placeholderText}
                    defaultValue={defaultValueTemplate}
                    onChange={(e) => { setEditedTextfieldValue(e.target.value) }}
                    style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        backgroundColor: "#f7f7f7",
                        border: "1px solid #ccc",
                        padding: "10px",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap", // Preserve whitespace for formatting

                    }}
                />
            </>
        );
    }

    const handleEditTemplateEvent = (idx) => {
        const newEventsList = curEvents || [];
        const updatingElem = newEventsList.find(elem => elem.uniqueId === idx);

        setEditedEventTemplate(updatingElem?.templates || "");
        setEditedEventTemplateNewVersion(updatingElem?.main_templates || "");
        setModalContent(defaultModalContent(idx, updatingElem?.templates));
        setModalState(true);
    }


    const updateEditedTemplate = (template = {}) => {
        setEditedEventTemplate(JSON.stringify(template));
        setEditedEventTemplateNewVersion(template);
    }

    // Modal window functions
    const handleOk = () => {
        try {
            setEditedEventTemplate(jsonrepair(editedTextfieldValue));
            setModalState(false);
        }
        catch (err) {
            error("Incorrect value! Please check the JSON object and try again.");
        }
    };
    const handleCancel = () => setModalState(false);


    // Create options for hostname dropdown
    const companyDomainsOptions = companyDomains.map((domain) => {
        return {
            value: domain.domain,
            label: domain.domain,
        }
    });

    companyDomainsOptions.unshift({
        value: "All domains",
        label: "All domains",
    });


    // update the selectedCompanyDoamain when switching between the companies
    useEffect(() => setSelectedCompanyDoamain("All domains"), [companyDomains, companyId, dlEvents])


    useEffect(() => {
        switch (selectedCompanyDoamain) {
            case "All domains":
                setFilteredCurEvents(curEvents)
                break;
            default:
                const newCurEvents = curEvents.filter(item => item.urls.includes(selectedCompanyDoamain));
                setFilteredCurEvents(newCurEvents)
        }
    }, [selectedCompanyDoamain, curEvents]);


    return (
        <>
            <Modal
                centered
                key={editedEventTemplate}
                title={modalTitle}
                open={modalState}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={(_, { OkBtn, CancelBtn }) => (
                    <Flex gap={5}>
                        <CancelBtn />
                        <OkBtn />
                    </Flex>
                )}>
                {modalContent}
            </Modal>
            <div key={`${companyId}_event_list`} className={`modal-elems-rows ${modalContentCode === "main" ? "visible" : "hidden"}`}>
                <div>
                    <h2 className='header-letter-spacing'>Monitoring via scraped events</h2>
                </div>
                <div className="modal-elems">
                    <div className="container-guide modal-panel-button-row">
                        <Link className="modal-guide-banner blue-bg" style={{ borderColor: colors.ccBlue }} target="_blank" to={scrapedEventsDescLink}>
                            <div className="file-icon" style={{ color: colors.ccBlue }}>
                                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g filter="url(#filter0_d_190_157)">
                                        <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                        <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                        <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                        <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                    </g>
                                    <defs>
                                        <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                            <feOffset dy="1.74496" />
                                            <feGaussianBlur stdDeviation="0.872482" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                        </filter>
                                    </defs>
                                </svg>
                            </div>
                            <div className="panel-button-text">
                                <div className="panel-button-desc">Guide how to set-up events</div>
                            </div>
                        </Link>
                    </div>
                </div>
                <div>
                    <div className="dropdown domains-dropdown">
                        <ConfigProvider>
                            <Select
                                labelInValue
                                className="transparent-border"
                                defaultValue={{
                                    value: 'All domains',
                                    label: 'All domains',
                                }}
                                style={{
                                    width: 200,
                                    border: '1px solid #d9d9d9',
                                    background: '#ffffff',
                                    borderRadius: 6
                                }}
                                onChange={(e) => setSelectedCompanyDoamain(e.value)}
                                options={companyDomainsOptions}
                            />
                        </ConfigProvider>

                    </div>
                    <p className='fst-italic pt-2'>
                        Make sure domain is added to domain list in Product settings
                    </p>
                </div>
                <div>
                    <h3 className="modal-category">Overview of your dataLayer events</h3>
                    <p>Update, add or delete the dataLayer events that need to be monitored.</p>
                </div>
                {filteredCurEvents.length > 0 && (
                    <div className="elems-list">
                        <div>

                            <div className="temp-row row-labels">
                                <div>Type event</div>
                                <div>Event name</div>
                                {adminUser && (
                                    <div>
                                        Status
                                    </div>
                                )}
                                <div></div>
                            </div>
                            <div
                                className="page-separator"
                                style={adminUser ? { width: "70%" } : { width: "50%" }}>
                            </div>

                            <div className="list-col-2-add-dl">
                                {filteredCurEvents?.map((elem, idx) => {
                                    return (
                                        <div key={idx}>
                                            <div className={adminUser ? "temp-row-admin-user" : "temp-row"}>
                                                <div>{elem?.event_type || ''}</div>
                                                <div>{elem?.event_name || ''}</div>
                                                {adminUser && (
                                                    <div>{elem?.event_status || ''}</div>
                                                )}
                                                <div>
                                                    <Pen title="Edit event template" onClick={() => handleEditEventClick(elem, elem?.uniqueId)} size={18}></Pen>
                                                </div>
                                            </div>
                                            <div
                                                className="page-separator"
                                                style={adminUser ? { width: "70%" } : { width: "50%" }}>
                                            </div>
                                        </div>

                                    );
                                })}
                            </div>
                        </div>

                    </div>
                )}
                <div>
                    <div
                        className="btn btn-sm cc-btn-outline-blue"
                        onClick={eventAdd}>
                        + Add event
                    </div>
                </div>

            </div>
            <div key={`${companyId}_event_edit`} className={`modal-elems-rows ${modalContentCode === "edit" ? "visible" : "hidden"}`}>

                <div>
                    <h2 className="header-submodal header-letter-spacing">
                        DataLayer events via Overview DataLayer Guard scenario’s
                    </h2>
                </div>

                <div className="modal-elems">
                    <div className="container-guide modal-panel-button-row">
                        <Link className="modal-guide-banner blue-bg" style={{ borderColor: colors.ccBlue }} target="_blank" to={scrapedEventsDescLink}>
                            <div className="file-icon" style={{ color: colors.ccBlue }}>
                                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g filter="url(#filter0_d_190_157)">
                                        <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                        <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                        <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                        <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                    </g>
                                    <defs>
                                        <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                            <feOffset dy="1.74496" />
                                            <feGaussianBlur stdDeviation="0.872482" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                        </filter>
                                    </defs>
                                </svg>


                            </div>
                            <div className="panel-button-text">
                                <div className="panel-button-desc">Guide how to set-up events</div>
                            </div>
                        </Link>
                    </div>
                </div>


                <div>
                    <p className="modal-category fw-bold mb-0" onClick={handleHeaderClick}>
                        <ArrowLeft size={24} className='pe-2' />
                        Overview dataLayer events
                    </p>
                </div>

                <div>
                    <div>
                        <h3 className='fw-bold mb-2 modal-category'>Event name</h3>
                        <Input className='bordered-input' value={editedEventName} placeholder="page_view"
                            onChange={(e) => setEditedEventName(e.target.value)}
                            style={{ width: "25%" }}
                        />

                    </div>
                </div>
                <div>
                    <h3 className="modal-category fw-bold">
                        Add event details
                    </h3>
                    <p className='mb-0'>
                        To enable monitoring for this event, details on the dataLayer event should be
                        shared in the table below.
                        <br></br>
                        For a quick start you can can copy / paste your JSON object via the button below.
                    </p>
                </div>

                <Flex gap={10} className="action-buttons">
                    <ActionButton
                        text={jsonBtnText}
                        type=""
                        icon={jsonBtnIcon}
                        onClickFunction={() => handleEditTemplateEvent(editedEventIdx)}
                        width={120}
                        className="custom-button"
                    />
                    <DropdownMenu
                        text="Add ecommerce event"
                        type="primary"
                        disabled={isTemplateObjectEmpty}
                        menuProps={menuProps}
                        loading={false}
                        className="custom-button"
                    />
                    <ActionButton
                        text="Clean JSON"
                        type=""
                        icon={<ArrowClockwise />}
                        onClickFunction={() => handleCleanJsonTemplate()}
                        className="custom-button"
                    />
                </Flex>


                {editedEventIdx &&
                    <div>

                        <NestedObjectTable
                            data={typeof editedEventTemplate === "string" ? editedEventTemplate : JSON.stringify(editedEventTemplate)}
                            updateTemplateFunction={updateEditedTemplate}
                            setEditedEventTemplateNewVersion={setEditedEventTemplateNewVersion}
                        />

                    </div>
                }


                <div>
                    <h3 className="fw-bold mt-4 modal-category">
                        Define trigger for dataLayer event
                    </h3>
                    <p className='mb-3'>
                        Define how and where the dataLayer event can be found via the fields below.
                    </p>
                </div>

                <table id='triggers-table'>
                    <tbody>
                        <tr>
                            <td>
                                <p className='fw-bold mb-0'>How does this event fire?</p>
                            </td>
                            <td>
                                <Select
                                    style={{ width: '100%' }}
                                    value={editedEventType}
                                    onChange={(value) => setEditedEventType(value)}
                                    popupClassName="custom-dropdown"
                                >
                                    {Object.keys(typesOptions).map((key) => (
                                        <Option key={key} value={key}>
                                            {typesOptions[key]}
                                        </Option>
                                    ))}
                                </Select>
                            </td>
                            <td></td>
                            <td></td>
                        </tr>
                        <tr>
                            <td>
                                <p className='fw-bold mb-0'>Fires on URL</p>
                            </td>
                            <td>
                                <Select
                                    style={{ width: '100%' }}
                                    value={urlMatchType}
                                    onChange={(value) => setUrlMatchType(value)}
                                    popupClassName="custom-dropdown"
                                >
                                    {Object.keys(urlOptions).map((key) => (
                                        <Option key={key} value={key}>
                                            {urlOptions[key]}
                                        </Option>
                                    ))}
                                </Select>
                            </td>
                            <td>
                                <Input value={editedEventUrls} placeholder="https://code-cube.io/"
                                    onChange={(e) => setEditedEventUrls(e.target.value)} />
                            </td>
                            <td></td>
                        </tr>

                        <tr className={editedEventType !== "interaction" ? `content-muted` : ''}>
                            <td>
                                <p className='fw-bold mb-0'>On which click element?</p>
                            </td>
                            <td>
                                <Select
                                    style={{ width: '100%' }}
                                    value={element}
                                    onChange={(value) => setElement(value)}
                                    disabled={editedEventType !== "interaction"}
                                    popupClassName="custom-dropdown"
                                >
                                    {Object.keys(elementOptions).map((key) => (
                                        <Option key={key} value={key}>
                                            {elementOptions[key]}
                                        </Option>
                                    ))}
                                </Select>
                            </td>
                            <td>
                                <Input value={elementRegexValue} placeholder="regex value"
                                    onChange={(e) => setElementRegexValue(e.target.value)}
                                    disabled={editedEventType !== "interaction"}
                                />
                            </td>
                            <td>
                                <Select
                                    style={{ width: '100%' }}
                                    value={elementMatchType}
                                    onChange={(value) => setElementMatchType(value)}
                                    disabled={editedEventType !== "interaction"}
                                    popupClassName="custom-dropdown"
                                >
                                    {Object.keys(urlOptions).map((key) => (
                                        <Option key={key} value={key}>
                                            {urlOptions[key]}
                                        </Option>
                                    ))}
                                </Select>
                            </td>
                        </tr>
                    </tbody>
                </table>

                {adminUser && (
                    <Space direction="vertical" size="medium" block="true">
                        <h3 className="fw-bold mt-4 modal-category pb-1">
                            Status of the event
                        </h3>

                        <DropdownMenu
                            text={editedEventStatus || "Select status"}
                            type=""
                            disabled={false}
                            menuProps={statusesProps}
                            loading={false}
                            width={141}
                        />
                    </Space>
                )}

                <Space>
                    <ActionButton
                        text="Remove event"
                        type=""
                        icon={<Trash3 size={14} />}
                        onClickFunction={() => eventRemove(editedEventIdx)}
                        danger={true}
                    />
                </Space>
            </div>
        </>
    );
}


export const MonitoringModal = () => {
    const {
        databaseName,
        companyId,
        tagMangerEventsDescLink,
        transactionEvents,
        setTransactionEvents,
        setConfigParams,
        companyDomains,
        newTransTempVersionAvailable,
        pushDefaultEcommerceObject,
        cleanJsonObject,
        handleEventAdd,
        handleEventRemove,
        error,
    } = useContext(MonitoringContext);

    const [curEvents, setCurEvents] = useState(addUniqueIdRows(transactionEvents));
    const [configParamsToSave, setConfigParamsToSave] = useState({});
    const [selectedCompanyDoamain, setSelectedCompanyDoamain] = useState("All domains");
    const [filteredCurEvents, setFilteredCurEvents] = useState(curEvents);

    const { adminUser } = useContext(AuthContext);

    const [editedEventIdx, setEditedEventIdx] = useState(null);
    const [editedEventName, setEditedEventName] = useState("");
    const [element, setElement] = useState("");
    const [urlMatchType, setUrlMatchType] = useState("");
    const [elementMatchType, setElementMatchType] = useState("");
    const [elementRegexValue, setElementRegexValue] = useState("");
    const [editedEventType, setEditedEventType] = useState("");
    const [editedEventStatus, setEditedEventStatus] = useState("");
    const [editedEventUrls, setEditedEventUrls] = useState("");
    const [editedEventTemplate, setEditedEventTemplate] = useState("");
    const [editedEventTemplateNewVersion, setEditedEventTemplateNewVersion] = useState("");

    const [modalContentCode, setModalContentCode] = useState("main");

    const [modalState, setModalState] = useState(false);
    const [modalTitle, setModalTitle] = useState("");
    const [modalContent, setModalContent] = useState(<></>);
    const [editedTextfieldValue, setEditedTextfieldValue] = useState("");

    const [jsonBtnText, setJsonBtnText] = useState("Add JSON");
    const [jsonBtnIcon, setJsonBtnIcon] = useState(<PlusLg />);

    const inputRefDataset = useRef(null);

    const copyToClipboard = (text) => {
        const inputValue = text.current.value;
        navigator.clipboard.writeText(inputValue);
    }

    // Add ecommerce event button functionality
    const [isTemplateObjectEmpty, setIsTemplateObjectEmpty] = useState(false);
    useEffect(() => {
        switch (editedEventTemplateNewVersion) {
            case "":
                setIsTemplateObjectEmpty(true);
                break;
            default:
                setIsTemplateObjectEmpty(editedEventTemplateNewVersion?.length > 1);
        }
    }, [editedEventTemplate, editedEventTemplateNewVersion]);

    const menuProps = {
        items: defaultMenuProps,
        onClick: (e) => handlePushDefaultEcommerceObject(e?.key, editedEventIdx),
    };

    const handlePushDefaultEcommerceObject = (key, idx) => {
        const curEventsItem = curEvents.find(elem => elem.uniqueId === idx);
        const newTemplates = pushDefaultEcommerceObject(key, idx, curEventsItem);

        setEditedEventTemplate(newTemplates);
        setEditedEventTemplateNewVersion(transformDataObject(newTemplates));
    }

    const handleCleanJsonTemplate = () => {
        const curEventsItem = curEvents.find(elem => elem.uniqueId === editedEventIdx);
        cleanJsonObject(editedEventIdx, curEventsItem);

        setEditedEventTemplate("");
        setEditedEventTemplateNewVersion("");
    }


    useEffect(() => {
        // check if the new template version exist if not update the events to add it
        if (!newTransTempVersionAvailable) {
            transactionEvents?.forEach((event) => {
                const templatesCopy = event.templates;
                let parsedTemplate;

                switch (typeof templatesCopy) {
                    case "string":
                        const repairedJson = templatesCopy && templatesCopy !== '' ? jsonrepair(templatesCopy) : '';
                        parsedTemplate = repairedJson !== '' ? JSON5.parse(repairedJson) : {};
                        break;
                    default:
                        parsedTemplate = templatesCopy;
                }

                const newVersionTemplateObject = transformDataObject(parsedTemplate);
                event.main_templates = newVersionTemplateObject;
            })
        }

        setCurEvents(addUniqueIdRows(transactionEvents) || []);
        setFilteredCurEvents(addUniqueIdRows(transactionEvents) || []);
        setConfigParamsToSave({ transaction_templates: { "events": transactionEvents } });
    }, [transactionEvents]);


    const updateParamsToSave = (params) => {
        const { newEventsList } = params;
        const newConfigParams = { ...configParamsToSave };
        const curEventsListStr = { "events": newEventsList };
        newConfigParams.transaction_templates = curEventsListStr;

        setConfigParamsToSave(newConfigParams);
    }


    const handleEditEventClick = (elem, uniqueId) => {
        setEditedEventIdx(uniqueId);
        setEditedEventName(elem?.event_name || "");
        setElement(elem?.element || "");
        setUrlMatchType(elem?.url_match_type || "");
        setElementMatchType(elem?.element_match_type || "");
        setElementRegexValue(elem?.element_regex_value || "");
        setEditedEventType(elem?.event_type || "");
        setEditedEventStatus(elem?.event_status || "");
        setEditedEventUrls(elem?.urls || "");
        setEditedEventTemplate(elem?.main_templates || elem?.templates || "");
        setEditedEventTemplateNewVersion(elem?.main_templates || "");
        setModalContentCode("edit");
    }

    // update the useStatet parameters when switching between companies
    useEffect(() => {
        setEditedEventIdx();
        setEditedEventName("");
        setElement("");
        setUrlMatchType("");
        setElementMatchType("");
        setElementRegexValue("");
        setEditedEventType("");
        setEditedEventStatus("");
        setEditedEventUrls("");
        setEditedEventTemplate("");
        setEditedEventTemplateNewVersion("");
        setModalContentCode("main");
    }, [companyId]);

    useEffect(() => {
        const newEventsList = curEvents || [];
        const updatingElem = newEventsList.find(elem => elem.uniqueId === editedEventIdx);

        if (updatingElem) {
            updatingElem.event_name = editedEventName;
            updatingElem.element = element;
            updatingElem.url_match_type = urlMatchType;
            updatingElem.element_match_type = elementMatchType;
            updatingElem.element_regex_value = elementRegexValue;
            updatingElem.event_type = editedEventType;
            updatingElem.event_status = editedEventStatus;
            updatingElem.urls = editedEventUrls;
            updatingElem.templates = editedEventTemplate;
            updatingElem.main_templates = editedEventTemplateNewVersion;

            setCurEvents(newEventsList);
            setTransactionEvents(newEventsList);
            updateParamsToSave({ newEventsList });
        }

        const template = editedEventTemplate !== "" &&
            typeof (editedEventTemplate) === "string" ? JSON5.parse(jsonrepair(editedEventTemplate)) : editedEventTemplate
        const initialTemplate = template?.length === 1 && template[0].key === "" && template[0].value === "";
        const text = initialTemplate ? "Add JSON" : "Edit JSON";
        const icon = initialTemplate ? <PlusLg /> : <Pen />;
        setJsonBtnText(text);
        setJsonBtnIcon(icon)

        setModalContent(defaultModalContent(editedEventIdx, editedEventTemplate));

    }, [modalContentCode, editedEventName,
        editedEventType, editedEventStatus,
        editedEventUrls, editedEventTemplate,
        urlMatchType, element, elementMatchType,
        elementRegexValue
    ]);

    const handleHeaderClick = () => {
        // removes empty events from the list of events
        const newEventsList = curEvents?.filter((elem) =>
            elem.element !== "" ||
            elem.event_name !== "" ||
            elem.urls !== "" ||
            elem.element_regex_value !== "" ||
            elem.main_templates?.length > 1
        );

        setCurEvents(() => newEventsList);
        setTransactionEvents(newEventsList);
        updateParamsToSave({ newEventsList });
        setModalContentCode("main");
    };

    const urlOptions = {
        "matches_regex": "matches regex",
        "contains ": "contains ",
        "exact_matches": "exact matches ",
    }

    const statusesOptions = [
        {
            label: "Status",
            key: "",
        },
        {
            label: "active",
            key: "active",
        },
        {
            label: "inactive",
            key: "inactive",
        },
    ];
    const statusesProps = {
        items: statusesOptions,
        onClick: (e) => setEditedEventStatus(e?.key),
    };

    const eventAdd = () => {
        const {
            newEvent,
            newEventsList,
            newEventUniqueId,
        } = handleEventAdd();

        setEditedEventTemplate(null);
        setEditedEventTemplateNewVersion(null);
        setCurEvents(newEventsList);
        setModalContentCode("edit");
        updateParamsToSave({ newEventsList });
        handleEditEventClick(newEvent, newEventUniqueId);
    }

    const eventRemove = (idx) => {
        const newEventsList = handleEventRemove(idx);

        setCurEvents(() => newEventsList);
        setModalContentCode("main");
        updateParamsToSave({ newEventsList });
    }

    useEffect(() => setConfigParams(configParamsToSave), [configParamsToSave]);

    // Center modal functionality
    const defaultModalContent = (idx, templates = "") => {
        let defaultValueTemplate = "";
        let parsedTemplate = {};

        const placeholderObj = {
            "event": "event_name",
            "ecommerce": {
                "currency": "string",
                "value": "number",
                "items": [
                    {
                        "item_id": "string",
                        "item_name": "string",
                    }
                ]
            }
        };
        const placeholderText = JSON.stringify(placeholderObj, undefined, 2);

        switch (typeof templates) {
            case "object":
                parsedTemplate = templates;
                break;
            default:
                const repairedJson = templates && templates !== '' ? jsonrepair(templates) : '';
                parsedTemplate = repairedJson !== '' ? JSON5.parse(repairedJson) : {};
        }

        const templateIsArray = Array.isArray(parsedTemplate);

        switch (templateIsArray) {
            case true:
                const tempLn = parsedTemplate.length;

                if (tempLn === 0) {
                    defaultValueTemplate = "";
                }
                else if (tempLn === 1 && parsedTemplate[0].key === "" &&
                    parsedTemplate[0].value === "") {
                    defaultValueTemplate = "";
                }
                else if (Object.keys(parsedTemplate).length > 0) {
                    const simplifiedDLGEventJson = simplifyDLGEventJson(parsedTemplate);
                    defaultValueTemplate = JSON.stringify(simplifiedDLGEventJson, null, 2);
                }
                break;
            default:
                defaultValueTemplate = JSON.stringify(parsedTemplate, null, 2);
        }

        setModalTitle("Template DataLayer object");

        return (
            <>
                <p>Add your dataLayer template in the box below.</p>
                <TextArea
                    key={idx}
                    rows={12}
                    placeholder={placeholderText}
                    defaultValue={defaultValueTemplate}
                    onChange={(e) => { setEditedTextfieldValue(e.target.value) }}
                    style={{
                        fontFamily: "monospace",
                        fontSize: "14px",
                        backgroundColor: "#f7f7f7",
                        border: "1px solid #ccc",
                        padding: "10px",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap", // Preserve whitespace for formatting
                    }}
                />
            </>
        );
    }

    const handleEditTemplateEvent = (idx) => {
        const newEventsList = curEvents || [];
        const updatingElem = newEventsList.find(elem => elem.uniqueId === idx);

        setEditedEventTemplate(updatingElem?.templates || "");
        setEditedEventTemplateNewVersion(updatingElem?.main_templates || "");
        setModalContent(defaultModalContent(idx, updatingElem?.templates));
        setModalState(true);
    }


    const updateEditedTemplate = (template = {}) => {
        setEditedEventTemplate(JSON.stringify(template));
        setEditedEventTemplateNewVersion(template);
    }

    // Modal window functions
    const handleOk = () => {
        try {
            setEditedEventTemplate(jsonrepair(editedTextfieldValue));
            setModalState(false);
        }
        catch (err) {
            error("Incorrect value! Please check the JSON object and try again.");
        }
    };
    const handleCancel = () => setModalState(false);


    // Create options for hostname dropdown
    const companyDomainsOptions = companyDomains.map((domain) => {
        return {
            value: domain.domain,
            label: domain.domain,
        }
    });

    companyDomainsOptions.unshift({
        value: "All domains",
        label: "All domains",
    });


    // update the selectedCompanyDoamain when switching between the companies
    useEffect(() => setSelectedCompanyDoamain("All domains"), [companyDomains, companyId, transactionEvents])

    useEffect(() => setModalContent(defaultModalContent(editedEventIdx, editedEventTemplate)), [editedEventTemplate])


    useEffect(() => {
        switch (selectedCompanyDoamain) {
            case "All domains":
                setFilteredCurEvents(curEvents);
                break;
            default:
                const newCurEvents = curEvents.filter(item => item.urls.includes(selectedCompanyDoamain));
                setFilteredCurEvents(newCurEvents);
        }
    }, [selectedCompanyDoamain, curEvents]);


    return (
        <>
            <Modal
                centered
                key={editedEventTemplate}
                title={modalTitle}
                open={modalState}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={(_, { OkBtn, CancelBtn }) => (
                    <Flex gap={5}>
                        <CancelBtn />
                        <OkBtn />
                    </Flex>
                )}>
                {modalContent}
            </Modal>
            <div key={`${companyId}_event_list`} className={`modal-elems-rows ${modalContentCode === "main" ? "visible" : "hidden"}`}>
                <div>
                    <h2 className='header-letter-spacing'>Monitoring via Tag Manager</h2>
                </div>
                <div className="modal-elems">
                    <div className="container-guide modal-panel-button-row">
                        <Link className="modal-guide-banner blue-bg" style={{ borderColor: colors.ccBlue }} target="_blank" to={tagMangerEventsDescLink}>
                            <div className="file-icon" style={{ color: colors.ccBlue }}>
                                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g filter="url(#filter0_d_190_157)">
                                        <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                        <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                        <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                        <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                    </g>
                                    <defs>
                                        <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                            <feOffset dy="1.74496" />
                                            <feGaussianBlur stdDeviation="0.872482" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                        </filter>
                                    </defs>
                                </svg>
                            </div>
                            <div className="panel-button-text">
                                <div className="panel-button-desc">Guide how to set-up events</div>
                            </div>
                        </Link>
                    </div>
                </div>
                <div>
                    <div className="dropdown domains-dropdown">
                        <ConfigProvider>
                            <Select
                                labelInValue
                                className="transparent-border"
                                defaultValue={{
                                    value: 'All domains',
                                    label: 'All domains',
                                }}
                                style={{
                                    width: 200,
                                    border: '1px solid #d9d9d9',
                                    background: '#ffffff',
                                    borderRadius: 6
                                }}
                                onChange={(e) => setSelectedCompanyDoamain(e.value)}
                                options={companyDomainsOptions}
                            />
                        </ConfigProvider>

                    </div>
                    <p className='fst-italic pt-2'>
                        Make sure domain is added to domain list in Product settings
                    </p>
                </div>

                <div>
                    <h3 className="modal-category">Database name for your configuration</h3>
                    <p style={{
                        marginBottom: 1,
                        maxWidth: '42rem',
                        width: '100%',
                        overflowWrap: 'break-word'
                    }}>
                        When implementing the new monitoring tag, the field "Dataset name" needs to be added manually.
                        Copy the name in the field below and add this value to your tag template.
                    </p>

                    <div className='d-flex flex-row' style={{ gap: '2em' }}>
                        <div>
                            <p className="modal-category fw-bold mt-2">Dataset name</p>
                            <div>
                                <input
                                    className='disabled-input'
                                    type="text"
                                    placeholder="Database name"
                                    value={databaseName || ""}
                                    disabled
                                    ref={inputRefDataset}
                                />
                                <Copy
                                    className='copy-icon'
                                    size={21}
                                    onClick={() => copyToClipboard(inputRefDataset)}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <div>
                    <h3 className="modal-category">Overview of your dataLayer events</h3>
                    <p>Update, add or delete the dataLayer events that need to be monitored.</p>
                </div>
                {filteredCurEvents.length > 0 && (
                    <div className="elems-list">
                        <div>

                            <div className="temp-row row-labels">
                                <div>Event name</div>
                                {adminUser && (
                                    <div>
                                        Status
                                    </div>
                                )}
                                <div>URL</div>
                                <div></div>
                            </div>
                            <div
                                className="page-separator"
                                style={adminUser ? { width: "70%" } : { width: "50%" }}>
                            </div>

                            <div className="list-col-2-add-dl">
                                {filteredCurEvents?.map((elem, idx) => {
                                    return (
                                        <div key={idx}>
                                            <div className={adminUser ? "temp-row-admin-user" : "temp-row"}>
                                                <div>{elem?.event_name || ''}</div>
                                                {adminUser && (
                                                    <div>{elem?.event_status || ''}</div>
                                                )}
                                                <div>{elem?.urls || ''}</div>
                                                <div>
                                                    <Pen title="Edit event template" onClick={() => handleEditEventClick(elem, elem?.uniqueId)} size={18}></Pen>
                                                </div>
                                            </div>
                                            <div
                                                className="page-separator"
                                                style={adminUser ? { width: "70%" } : { width: "50%" }}>
                                            </div>
                                        </div>

                                    );
                                })}
                            </div>
                        </div>

                    </div>
                )}
                <div>
                    <div
                        className="btn btn-sm cc-btn-outline-blue"
                        onClick={eventAdd}>
                        + Add event
                    </div>
                </div>

            </div>
            <div key={`${companyId}_event_edit`} className={`modal-elems-rows ${modalContentCode === "edit" ? "visible" : "hidden"}`}>

                <div>
                    <h2 className="header-submodal header-letter-spacing">
                        Overview DataLayer Guard scenario’s
                    </h2>
                </div>

                <div className="modal-elems">
                    <div className="container-guide modal-panel-button-row">
                        <Link className="modal-guide-banner blue-bg" style={{ borderColor: colors.ccBlue }} target="_blank" to={tagMangerEventsDescLink}>
                            <div className="file-icon" style={{ color: colors.ccBlue }}>
                                <svg width="26" height="31" viewBox="0 0 26 31" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <g filter="url(#filter0_d_190_157)">
                                        <path d="M2.64922 3.41406H16.4948V7.60147C16.4948 7.95999 16.7855 8.25062 17.144 8.25062H19.3979V26.6164C19.3979 26.975 19.1073 27.2656 18.7488 27.2656H2.64922C2.2907 27.2656 2.00006 26.975 2.00006 26.6164V4.06322C2.00006 3.7047 2.2907 3.41406 2.64922 3.41406Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M3.72516 24.6433H16.4543C16.4543 24.8469 16.6193 25.0119 16.8228 25.0119H19.4347V25.8118C19.4347 26.1703 19.1441 26.4609 18.7856 26.4609H4.37431C4.0158 26.4609 3.72516 26.1703 3.72516 25.8118V24.6433Z" fill="#69C4FF" fillOpacity="0.5" />
                                        <path d="M4.34194 1.79309H18.3155V5.98808C18.3155 6.3466 18.6061 6.63724 18.9646 6.63724H21.7393V25.0329C21.7393 25.3914 21.4487 25.682 21.0902 25.682H4.34194C3.98342 25.682 3.69278 25.3914 3.69278 25.0329V2.44225C3.69278 2.08373 3.98342 1.79309 4.34194 1.79309Z" fill="#DBF1FF" />
                                        <path d="M5.41306 22.566H18.6682C18.6682 22.755 18.8214 22.9082 19.0104 22.9082H21.7718V23.6046C21.7718 23.9631 21.4811 24.2538 21.1226 24.2538H6.06221C5.70369 24.2538 5.41306 23.9631 5.41306 23.6046V22.566Z" fill="#B0E0FF" />
                                        <path d="M6.06221 0.170166H18.7207V4.25985C18.7207 4.61837 19.0114 4.909 19.3699 4.909H23.46V22.6673C23.46 23.1492 23.0694 23.5398 22.5875 23.5398H6.06221C5.70369 23.5398 5.41306 23.2491 5.41306 22.8906V0.819322C5.41306 0.460803 5.70369 0.170166 6.06221 0.170166Z" fill="white" />
                                        <line x1="8.82252" y1="9.40248" x2="19.988" y2="9.40248" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="15.1451" x2="20.0685" y2="15.1451" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="11.3499" x2="19.988" y2="11.3499" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.90306" y1="17.0925" x2="20.0685" y2="17.0925" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <line x1="8.82252" y1="13.1675" x2="19.988" y2="13.1675" stroke="#009BFF" strokeOpacity="0.14" strokeWidth="0.389493" strokeLinecap="round" />
                                        <path d="M19.3703 4.946C19.0118 4.94601 18.7212 4.65537 18.7212 4.29685L18.7212 0.170195L23.4599 4.94597L19.3703 4.946Z" fill="#69C4FF" fillOpacity="0.14" />
                                    </g>
                                    <defs>
                                        <filter id="filter0_d_190_157" x="0.255097" y="0.170166" width="24.9498" height="30.5854" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                                            <feFlood floodOpacity="0" result="BackgroundImageFix" />
                                            <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha" />
                                            <feOffset dy="1.74496" />
                                            <feGaussianBlur stdDeviation="0.872482" />
                                            <feComposite in2="hardAlpha" operator="out" />
                                            <feColorMatrix type="matrix" values="0 0 0 0 0.168627 0 0 0 0 0.168627 0 0 0 0 0.709804 0 0 0 0.15 0" />
                                            <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_190_157" />
                                            <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_190_157" result="shape" />
                                        </filter>
                                    </defs>
                                </svg>


                            </div>
                            <div className="panel-button-text">
                                <div className="panel-button-desc">Guide how to set-up events</div>
                            </div>
                        </Link>
                    </div>
                </div>


                <div>
                    <p className="modal-category fw-bold mb-0" onClick={handleHeaderClick}>
                        <ArrowLeft size={24} className='pe-2' />
                        Overview dataLayer events
                    </p>
                </div>

                <div>
                    <div>
                        <h3 className='fw-bold mb-2 modal-category'>Event name</h3>
                        <Input className='bordered-input' value={editedEventName} placeholder="page_view"
                            onChange={(e) => setEditedEventName(e.target.value)}
                            style={{ width: "25%" }}
                        />

                    </div>
                </div>
                <div>
                    <h3 className="modal-category fw-bold">
                        Add event details
                    </h3>
                    <p className='mb-0'>
                        To enable monitoring for this event, details on the dataLayer event should be
                        shared in the table below.
                        <br></br>
                        For a quick start you can can copy / paste your JSON object via the button below.
                    </p>
                </div>

                <Flex gap={10}>
                    <ActionButton
                        text={jsonBtnText}
                        type=""
                        icon={jsonBtnIcon}
                        onClickFunction={() => handleEditTemplateEvent(editedEventIdx)}
                        width={120}
                    />
                    <DropdownMenu
                        text="Add ecommerce event"
                        type="primary"
                        disabled={isTemplateObjectEmpty}
                        menuProps={menuProps}
                        loading={false}
                    />
                    <ActionButton
                        text="Clean JSON"
                        type=""
                        icon={<ArrowClockwise />}
                        onClickFunction={() => handleCleanJsonTemplate()}
                    />
                </Flex>

                {editedEventIdx &&
                    <div>

                        <NestedObjectTable
                            data={typeof editedEventTemplate === "string" ? editedEventTemplate : JSON.stringify(editedEventTemplate)}
                            updateTemplateFunction={updateEditedTemplate}
                            setEditedEventTemplateNewVersion={setEditedEventTemplateNewVersion}
                        />

                    </div>
                }


                <div>
                    <h3 className="fw-bold mt-4 modal-category">
                        Define trigger for dataLayer event
                    </h3>
                    <p className='mb-3'>
                        Define how and where the dataLayer event can be found via the fields below.
                    </p>
                </div>

                <table id='triggers-table'>
                    <tbody>
                        <tr>
                            <td>
                                <p className='fw-bold mb-0'>Fires on URL</p>
                            </td>
                            <td>
                                <Select
                                    style={{ width: '100%' }}
                                    value={urlMatchType}
                                    onChange={(value) => setUrlMatchType(value)}
                                    popupClassName="custom-dropdown"
                                >
                                    {Object.keys(urlOptions).map((key) => (
                                        <Option key={key} value={key}>
                                            {urlOptions[key]}
                                        </Option>
                                    ))}
                                </Select>
                            </td>
                            <td>
                                <Input value={editedEventUrls} placeholder="https://code-cube.io/"
                                    onChange={(e) => setEditedEventUrls(e.target.value)} />
                            </td>
                            <td></td>
                        </tr>

                    </tbody>
                </table>


                {adminUser && (
                    <Space direction="vertical" size="medium" block="true">
                        <h3 className="fw-bold mt-4 modal-category pb-1">
                            Status of the event
                        </h3>

                        <DropdownMenu
                            text={editedEventStatus || "Select status"}
                            type=""
                            disabled={false}
                            menuProps={statusesProps}
                            loading={false}
                            width={141}
                        />
                    </Space>
                )}

                <Space>
                    <ActionButton
                        text="Remove event"
                        type=""
                        icon={<Trash3 size={14} />}
                        onClickFunction={() => eventRemove(editedEventIdx)}
                        danger={true}
                    />
                </Space>
            </div>
        </>
    );
}
